// services/api.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

// Event emitter for global app events
class EventEmitter {
  private events: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  off(event: string, callback: Function) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  emit(event: string, ...args: any[]) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(...args));
  }
}

export const apiEvents = new EventEmitter();

class ApiService {
  private api: AxiosInstance;
  private refreshing = false;
  private failedQueue: any[] = [];

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8001/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.refreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.api(originalRequest);
              })
              .catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.refreshing = true;

          const refreshToken = localStorage.getItem('refresh_token');

          if (!refreshToken) {
            this.handleAuthError();
            return Promise.reject(error);
          }

          try {
            const response = await axios.post(
              `${this.api.defaults.baseURL}/refresh/`,
              { refresh: refreshToken }
            );

            const { access } = response.data;
            localStorage.setItem('token', access);
            this.setAuthToken(access);

            this.processQueue(null, access);
            this.refreshing = false;

            originalRequest.headers.Authorization = `Bearer ${access}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            this.refreshing = false;
            this.handleAuthError();
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        return this.handleError(error);
      }
    );
  }

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private handleAuthError() {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    apiEvents.emit('auth:unauthorized');
    apiEvents.emit('navigate', '/login');
  }

  private handleError(error: AxiosError): Promise<never> {
    const response: any = error.response;

    if (!response) {
      apiEvents.emit('toast:error', 'Network error. Please check your connection.');
      return Promise.reject({ message: 'Network error' });
    }

    const status = response.status;
    const data = response.data;

    // Handle different error types
    switch (status) {
      case 400:
        // Do NOT emit a toast for 400s — the calling form reads err.data
        // and surfaces each DRF field error next to the relevant input.
        break;
      case 403:
        apiEvents.emit('toast:error', 'You do not have permission to perform this action');
        break;
      case 404:
        apiEvents.emit('toast:error', 'Resource not found');
        break;
      case 500:
        apiEvents.emit('toast:error', 'Server error. Please try again later.');
        break;
      default:
        apiEvents.emit('toast:error', data.message || 'An error occurred');
    }

    return Promise.reject({
      status,
      message: data.message || data.detail || 'An error occurred',
      data: data,
    });
  }

  setAuthToken(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken() {
    delete this.api.defaults.headers.common['Authorization'];
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.api.get(url, {
      ...config,
      responseType: 'blob', // Force the response type
    });
    return response.data; // This is the actual Blob
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  // Specialized method for file downloads
  async download(url: string, filename: string, params?: any): Promise<void> {
    try {
      const response = await this.api.get(url, {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      apiEvents.emit('toast:success', 'File downloaded successfully');
    } catch (error) {
      console.log('error:',error);
      apiEvents.emit('toast:error', 'Failed to download file');
      throw error;
    }
  }
}

export const apiService = new ApiService();